import { faCheckCircle, faInfoCircle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, AlertTitle, Link, Skeleton} from "@mui/material";
import { useScopedField } from "@ngt/forms-core";
import { FC, useEffect, useRef, useState } from "react";
import useIsAdminSection from "@/hooks/useIsAdminSection";
import * as Dtos from '../api/dtos';
import useCapStatus from "@/hooks/useCapStatus";
import useDebouncedValue from "@/hooks/useDebouncedValue";

interface IUseCapStatus {
    speakerSection?: boolean;
}

const CapCheck: FC<IUseCapStatus> = ({
    speakerSection
}) => {
    const {state: {value: firstName}} = useScopedField<string>("firstName", {value: true});
    const {state: {value: lastName}} = useScopedField<string>("lastName", {value: true});

    const {context: {setValue: setRegistrationAffiliation}} = useScopedField<Dtos.RegistrationAffiliation>("registrationAffiliation", {value: true});

    const {state: {value: alreadyRegistered}} = useScopedField<boolean>("alreadyRegistered", {value: true});

    const debouncedFirstName = useDebouncedValue(firstName);
    const debouncedLastName = useDebouncedValue(lastName);
    const { isCap, loading } = useCapStatus(debouncedFirstName ?? undefined, debouncedLastName ?? undefined);

    const isAdminSection = useIsAdminSection();

    const [deferredLoading, setDeferredLoading] = useState(false);
    const timerRef = useRef<number>();

    useEffect(() => {
        if (loading) {
            timerRef.current = window.setTimeout(() => {
                setDeferredLoading(true);
            }, 250);
        }
        else {
            if (timerRef.current) {
                window.clearTimeout(timerRef.current)
                timerRef.current = undefined;
            }

            setDeferredLoading(false);
        }
    }, [loading, setDeferredLoading, timerRef])

    useEffect(() => {
        if(isCap){
            setRegistrationAffiliation(Dtos.RegistrationAffiliation.CAP);
        }
        
    }, [isCap, setRegistrationAffiliation])

    if (deferredLoading && loading) {
        return (
            <Alert
                icon={<FontAwesomeIcon icon={faInfoCircle} />}
                severity="info"
                sx={{
                    alignItems: 'center'
                }}
            >
                <AlertTitle>Membership Status &ndash; <Skeleton width={120} sx={{display: 'inline-block'}} /></AlertTitle>
                Checking membership status...
            </Alert>
        )
    }

    if (alreadyRegistered && !isAdminSection) {
        return null;
    }

    if (firstName && lastName) {
        return (
            <>
            {
                !isCap ? 
                    isAdminSection ?
                        <Alert
                            icon={<FontAwesomeIcon icon={faInfoCircle} />}
                            severity="info"
                            sx={{
                                alignItems: 'center'
                            }}
                        >
                            <AlertTitle>CAP Member Status &ndash; Not-CAP</AlertTitle>
                                Not a CAP Member
                        </Alert>

                    :
                        <Alert
                            icon={<FontAwesomeIcon icon={faInfoCircle} />}
                            severity="info"
                            sx={{
                                alignItems: 'center'
                            }}
                        >
                            <AlertTitle>CAP Member Status &ndash; Not-CAP</AlertTitle>
                            
                            {
                                speakerSection ?
                                    <>
                                        You are not a CAP Member.
                                    </>
                                :
                                    <>
                                        You are not a CAP Member. If this is incorrect, please contact BCT on <Link href="tel:0249255255">02 4925 5255</Link>.
                                    </>
                            }
                        </Alert>
                :
                    <Alert
                        icon={<FontAwesomeIcon icon={faCheckCircle} />}
                        severity="success"
                        sx={{
                            alignItems: 'center'
                        }}
                    >
                        <AlertTitle>CAP Member Status &ndash; CAP</AlertTitle>
                        You are a CAP member
                    </Alert>
            }
            </>
        )
    }

    return (
        <Alert
            icon={<FontAwesomeIcon icon={faInfoCircle} />}
            severity="info"
            sx={{
                alignItems: 'center'
            }}
        >
                <AlertTitle>CAP Member Status &ndash; Unknown</AlertTitle>
                Please enter your first and last name.
        </Alert>
    );
}

export default CapCheck;